import debounce from 'lodash/debounce';
import { logSafeErrorWithoutCustomerDataWrapper } from '@jira/polaris__lib-logging/src/logSafeErrorWithoutCustomerDataWrapper.tsx';

import type { FieldKey } from '@jira/polaris__domain-field/src/FieldKey.types.tsx';
import { experience } from '@jira/polaris__lib-analytics/src/ConstantsExperience.tsx';
import { isNotFoundError } from '@jira/polaris__lib-errors/src/isPermissionError.tsx';
import { autoBeforeEachTest } from '@jira/platform__utils-auto-before-each-test/src/auto-before-each-test.tsx';
import { isClientFetchError } from '@atlassian/jira-fetch/src/index.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { StoreActionApi } from '@atlassian/react-sweet-state';
import type { State } from './IssueType.types.tsx';
import { performUpdatePinnedFields } from './performUpdatePinnedFields.tsx';
import { performGetPinnedFieldsForType } from './performGetPinnedFieldsForType.tsx';

type IssueTypeId = string;

export const PINNED_FIELDS_UPDATE_DEBOUNCE_DELAY = 2000;

type DebouncedPerformUpdatePinnedFields = (args: {
	projectId: string;
	pinnedFields: FieldKey[];
	getState: StoreActionApi<State>['getState'];
	setState: StoreActionApi<State>['setState'];
}) => Promise<void>;

/**
 * Keeps the original state of pinned fields
 * (pre first unsent optimistic update) per issueTypeId.
 * Used for rolling back changes on error.
 */
const previousPinnedFieldsStateMap = new Map<IssueTypeId, FieldKey[]>();

/**
 * Promise handlers waiting for the (debounced) persist per issueTypeId.
 * Every call within the debounce window shares one network request; all
 * returned promises resolve / reject together once it fires (only the last
 * rejects in case of an error).
 */
const pendingPinnedFieldsPromiseHandlersMap = new Map<
	IssueTypeId,
	{ resolve: () => void; reject: (err: unknown) => void }[]
>();

/**
 * A map that tracks pending debounced updates for pinned fields by issue type.
 */
const pendingPinnedFieldsUpdatesMap = new Map<
	IssueTypeId,
	ReturnType<typeof debounce<DebouncedPerformUpdatePinnedFields>>
>();
autoBeforeEachTest(() => {
	previousPinnedFieldsStateMap.clear();
	pendingPinnedFieldsPromiseHandlersMap.clear();
	pendingPinnedFieldsUpdatesMap.clear();
});

/**
 * Build (or reuse) a debounced function for an issue type
 */
const getDebouncedPerformUpdatePinnedFields = (issueTypeId: IssueTypeId) => {
	let pendingPinnedFieldsUpdateForIssueType = pendingPinnedFieldsUpdatesMap.get(issueTypeId);

	if (pendingPinnedFieldsUpdateForIssueType) {
		return pendingPinnedFieldsUpdateForIssueType;
	}

	pendingPinnedFieldsUpdateForIssueType = debounce<DebouncedPerformUpdatePinnedFields>(
		async ({
			projectId,
			pinnedFields,
			getState,
			setState,
		}: {
			projectId: string;
			pinnedFields: FieldKey[];
			getState: StoreActionApi<State>['getState'];
			setState: StoreActionApi<State>['setState'];
		}) => {
			// Snapshot current waiting handlers (may be empty).
			const handlers = pendingPinnedFieldsPromiseHandlersMap.get(issueTypeId) ?? [];

			try {
				await performUpdatePinnedFields({ projectId, issueTypeId, pinnedFields });

				handlers.forEach(({ resolve }) => resolve());
				experience.issueTypes.issueTypePinnedFieldsUpdate.success();
			} catch (maybeError) {
				const error =
					maybeError instanceof Error
						? maybeError
						: new Error('Error while updating pinned fields');

				if (isClientFetchError(error) || isNotFoundError(error)) {
					experience.issueTypes.issueTypePinnedFieldsUpdate.abort(error);
				} else {
					experience.issueTypes.issueTypePinnedFieldsUpdate.failure(error);
				}

				// Roll back only pinnedFields (avoid stomping other parallel optimistic changes)
				const previousPinnedFieldsState = previousPinnedFieldsStateMap.get(issueTypeId);
				if (previousPinnedFieldsState) {
					const state = getState();
					if (state.issueTypesById[issueTypeId]) {
						setState({
							issueTypesById: {
								...state.issueTypesById,
								[issueTypeId]: {
									...state.issueTypesById[issueTypeId],
									pinnedFields: previousPinnedFieldsState,
								},
							},
						});
					}
				}

				logSafeErrorWithoutCustomerDataWrapper(
					'polaris.error.controllers.issue-types.actions.update-pinned-field',
					'Failed to update pinned fields',
					error,
				);

				// Reject only the last caller so that UI can show a single error screen;
				// resolve the rest (they already got optimistic rollback).
				handlers.forEach(({ reject, resolve }, index) => {
					if (index === handlers.length - 1) {
						reject(error);
					} else {
						resolve();
					}
				});
			} finally {
				pendingPinnedFieldsUpdatesMap.delete(issueTypeId);
				previousPinnedFieldsStateMap.delete(issueTypeId);
				pendingPinnedFieldsPromiseHandlersMap.delete(issueTypeId);
			}
		},
		PINNED_FIELDS_UPDATE_DEBOUNCE_DELAY,
	);

	pendingPinnedFieldsUpdatesMap.set(issueTypeId, pendingPinnedFieldsUpdateForIssueType);

	return pendingPinnedFieldsUpdateForIssueType;
};

export const updatePinnedFields =
	({ issueTypeId, pinnedFields }: { issueTypeId: IssueTypeId; pinnedFields: FieldKey[] }) =>
	({ setState, getState }: StoreActionApi<State>): Promise<void> => {
		return new Promise<void>((resolve, reject) => {
			const state = getState();
			try {
				const issueType = state.issueTypesById[issueTypeId];
				if (!issueType) {
					throw new Error(`Issue type with ID ${issueTypeId} not found`);
				}

				const projectId = Object.keys(state.issueTypesByProjectId).find((id) =>
					state.issueTypesByProjectId[id]?.includes(issueTypeId),
				);
				if (!projectId) {
					throw new Error(`Project ID for issue type ${issueTypeId} not found`);
				}

				// Take snapshot only the first time within a pending debounce window
				if (!previousPinnedFieldsStateMap.has(issueTypeId)) {
					previousPinnedFieldsStateMap.set(issueTypeId, issueType.pinnedFields ?? []);
					experience.issueTypes.issueTypePinnedFieldsUpdate.start();
				}

				setState({
					issueTypesById: {
						...state.issueTypesById,
						[issueTypeId]: {
							...issueType,
							pinnedFields,
						},
					},
				});

				// Register handlers for this cycle
				const handlers = pendingPinnedFieldsPromiseHandlersMap.get(issueTypeId);
				if (handlers) {
					handlers.push({ resolve, reject });
				} else {
					pendingPinnedFieldsPromiseHandlersMap.set(issueTypeId, [{ resolve, reject }]);
				}

				// Schedule / refresh debounced call
				getDebouncedPerformUpdatePinnedFields(issueTypeId)({
					projectId,
					pinnedFields,
					getState,
					setState,
				});
			} catch (error) {
				logSafeErrorWithoutCustomerDataWrapper(
					'polaris.error.controllers.issue-types.actions.update-pinned-field',
					'Failed to update pinned fields',
					error instanceof Error ? error : new Error('Error while updating pinned fields'),
				);

				reject(error);
			}
		});
	};

export const togglePinnedField =
	({ issueTypeId, fieldKey }: { issueTypeId: IssueTypeId; fieldKey: FieldKey }) =>
	async ({ getState, dispatch }: StoreActionApi<State>): Promise<void> => {
		const state = getState();
		const pinnedFields = state.issueTypesById[issueTypeId]?.pinnedFields ?? [];

		const isPinned = pinnedFields.includes(fieldKey);
		const updatedPinnedFields = isPinned
			? pinnedFields.filter((key) => key !== fieldKey)
			: [...pinnedFields, fieldKey];

		await dispatch(
			updatePinnedFields({
				issueTypeId,
				pinnedFields: updatedPinnedFields,
			}),
		);
	};

export const fetchPinnedFieldsForType =
	({ issueTypeId, projectId }: { issueTypeId: IssueTypeId; projectId: string }) =>
	async ({ setState, getState }: StoreActionApi<State>): Promise<void> => {
		try {
			experience.issueTypes.issueTypePinnedFieldsLoad.start();

			const { pinnedFields } = await performGetPinnedFieldsForType({ issueTypeId, projectId });
			const state = getState();

			if (state.issueTypesById[issueTypeId]) {
				setState({
					issueTypesById: {
						...state.issueTypesById,
						[issueTypeId]: {
							...state.issueTypesById[issueTypeId],
							pinnedFields,
						},
					},
				});
			}

			experience.issueTypes.issueTypePinnedFieldsLoad.success();
		} catch (maybeError) {
			const error =
				maybeError instanceof Error
					? maybeError
					: new Error('Error while fetching pinned fields for type');

			if (isClientFetchError(error)) {
				experience.issueTypes.issueTypePinnedFieldsLoad.abort(error);
			} else {
				experience.issueTypes.issueTypePinnedFieldsLoad.failure(error);
			}

			logSafeErrorWithoutCustomerDataWrapper(
				'polaris.error.controllers.issue-types.actions.fetch-pinned-fields',
				'Failed to fetch pinned fields for issue type',
				error,
			);

			throw error;
		}
	};
